import useSWR from 'swr';
import { useTenant } from '@parcel2go/npm-p2g-app';
import { PAYMENT_API } from '~/lib/constants';
import { authedFetch } from '~/lib/api/authed-fetch';
import { parseResult } from '~/lib/api/parse-result';
import { captureWarning } from '~/lib/metrics';
import { resolveTierFromProductName } from './resolve-tier';
import { PlanCard, SubscriptionPlansResult } from './types';

/** `idle` is a caller that asked for no catalogue - see `enabled` on {@link usePlanCards}. */
export type PlanCardsStatus = 'idle' | 'loading' | 'ready' | 'error';

/**
 * The catalogue IS the mapping. A Stripe price carries the product it belongs to and the interval it
 * bills on, so a card needs neither a hardcoded list of lookup keys nor a guess at what a key's
 * suffix means. That matters beyond tidiness: a key added or renamed in Stripe would silently fall
 * out of a mirrored list, and the failure mode is a tier the customer cannot buy.
 *
 * A plan is skipped when it cannot be presented honestly - no recognisable product, an interval
 * with no card shape (the daily trial price), or no amount to show.
 */
function toPlanCard(plan: SubscriptionPlansResult['plans'][number]): PlanCard | null {
  const tier = resolveTierFromProductName(plan.productName);
  if (!tier || (plan.interval !== 'month' && plan.interval !== 'year') || plan.unitAmount === null) {
    return null;
  }
  return {
    priceId: plan.priceId,
    tier,
    interval: plan.interval,
    lookupKey: plan.lookupKey,
    priceMinorUnits: plan.unitAmount,
    currency: plan.currency
  };
}

/**
 * The anonymous subscription-plans catalogue (`GET {PAYMENT_API}/pp/stripe/subscription-plans`),
 * grouped into Plus/Pro tier cards. Same endpoint ui-checkout's own plans page reads - no auth
 * required, so it is safe to call for a just-created account.
 *
 * Behind SWR on a tenant-scoped key because more than one surface wants it at once (the picker, and
 * the pending-plan comparison that has to know which price a lookup key names). The response is the
 * tenant's public price list, identical for every visitor, so it dedupes cleanly and does not need
 * revalidating while a customer sits on a page.
 *
 * `enabled: false` makes no request at all. For a caller mounted on pages that may never need the
 * catalogue - a tenant that sells no subscriptions must not be sent to the payments API just because
 * a hook that might consult it rendered.
 */
export function usePlanCards({ enabled = true }: { enabled?: boolean } = {}): {
  status: PlanCardsStatus;
  cards: PlanCard[];
} {
  const { tenant } = useTenant();

  const { data, error, isLoading } = useSWR(
    enabled && tenant ? `subscription-plans-${tenant}` : null,
    async () => {
      // Anonymous endpoint - an empty token override means authedFetch attaches no Authorization
      // header (canUseAccessToken('') is false) while still setting the x-tenant header we need.
      const response = await authedFetch(tenant, `${PAYMENT_API}/pp/stripe/subscription-plans`, {}, '');
      const parsed = await parseResult<SubscriptionPlansResult>(response);
      if (!parsed.success || !parsed.result) {
        throw new Error('Subscription plans response was unsuccessful');
      }
      return parsed.result.plans.map(toPlanCard).filter((card): card is PlanCard => card !== null);
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      onError: (e) => captureWarning('Subscription plans fetch failed', { section: 'subscriptions' }, [e])
    }
  );

  if (!enabled) {
    return { status: 'idle', cards: [] };
  }
  if (isLoading) {
    return { status: 'loading', cards: [] };
  }
  if (error || !data) {
    return { status: 'error', cards: [] };
  }
  return { status: 'ready', cards: data };
}

/** The catalogue entry a pending plan names, or `null` when the catalogue cannot identify it. */
export function findPlanCard(
  cards: PlanCard[],
  plan: { plan?: string; tier?: string; interval?: string }
): PlanCard | null {
  if (plan.plan) {
    const key = plan.plan.trim().toLowerCase();
    return cards.find((card) => card.lookupKey?.trim().toLowerCase() === key) ?? null;
  }
  return cards.find((card) => card.tier === plan.tier && card.interval === plan.interval) ?? null;
}
